import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  Rating,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  VideoLibrary,
  Description,
  EmojiObjects,
  Star,
  CheckCircle,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useVideoProgress } from '../hooks/useVideoProgress';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Button,
  Stack,
  EmptyState,
  Badge,
  Loading,
} from '../design-system';
import { colors, spacing, typography } from '../design-system';
import { getVideoUrlSAS } from '../services/analisisIndividual';
import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';

/**
 * AnalisisIndividualDetalleModern - Modern micro-concept detail page
 * Detailed view with video explanation and key points
 */
const AnalisisIndividualDetalleModern = () => {
  usePageTitle('Detalle Análisis');
  const { t } = useTranslation('analysis');
  const location = useLocation();
  const navigate = useNavigate();
  const microconcepto = location.state;
  const videoRef = useRef(null);
  const { user } = useAuth();
  const userId = user?.id || null;
  
  const [videoUrl] = useState(microconcepto?.videoExplicativoUrl || '');
  const [sasVideoUrl, setSasVideoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { watched, viewCount, secondsWatched, resetWatched } = useVideoProgress(
    userId,
    microconcepto?.id,
    videoRef,
    sasVideoUrl
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchVideoUrl = async () => {
      if (!videoUrl) return;
      
      setLoading(true);
      try {
        const sasUrl = await getVideoUrlSAS(
          videoUrl.replace(
            'https://analisisindividualvideos.blob.core.windows.net/analisisindividual/',
            ''
          ),
          navigate
        );
        setSasVideoUrl(sasUrl);
        window.scrollTo(0, 0);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchVideoUrl();
  }, [videoUrl, navigate]);

  if (!microconcepto) {
    return (
      <Container>
        <EmptyState
          type="error"
          title={t('detail.notFound')}
          message={t('detail.notFoundDesc')}
          action={
            <Button variant="contained" onClick={() => navigate('/analisisIndividual')}>
              {t('detail.back')}
            </Button>
          }
        />
      </Container>
    );
  }

  const getDifficultyColor = (difficulty) => {
    if (difficulty === 1) return colors.success[500];
    if (difficulty === 2) return colors.warning[500];
    return colors.error[500];
  };

  const getDifficultyLabel = (difficulty) => {
    if (difficulty === 1) return t('detail.difficulty.basic');
    if (difficulty === 2) return t('detail.difficulty.intermediate');
    return t('detail.difficulty.advanced');
  };

  return (
    <Box className="wp-page" sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Header */}
      <Box
        className="wp-heading"
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1, borderColor: 'divider',
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/analisisIndividual')} aria-label={t('detail.back')} sx={{ color: colors.primary[600] }}>
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography component="h1" variant="h4" fontWeight={700} color="text.primary">
                {microconcepto.conceptoMicro}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
                {microconcepto.tipo && (
                  <Badge variant="neutral">{microconcepto.tipo}</Badge>
                )}
                {microconcepto.posicion && (
                  <Badge variant="secondary">{microconcepto.posicion}</Badge>
                )}
                {microconcepto.Dificultad && (
                  <Box
                    sx={{
                      padding: `${spacing.xs} ${spacing.sm}`,
                      backgroundColor: getDifficultyColor(microconcepto.Dificultad),
                      borderRadius: `${spacing.borderRadius.sm}px`,
                      color: colors.neutral.white,
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.semibold,
                    }}
                  >
                    {getDifficultyLabel(microconcepto.Dificultad)}
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>

      <Container className="wp-body" sx={{ paddingY: `${spacing.xl}px` }}>
        <Grid container spacing={`${`${spacing.xl}px`}px`}>
          {/* Video Section */}
          <Grid item xs={12} lg={8}>
            <Stack spacing={`${`${spacing.lg}px`}px`}>
              {/* Video Player */}
              <Card>
                <CardHeader
                  title={t('detail.videoTitle')}
                  icon={<VideoLibrary />}
                />
                <CardContent>
                  {loading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 400,
                      }}
                    >
                      <Loading variant="circular" />
                    </Box>
                  ) : sasVideoUrl ? (
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        borderRadius: 0,
                        overflow: 'hidden',
                      }}
                    >
                      <video
                        ref={videoRef}
                        src={sasVideoUrl}
                        controls
                        style={{
                          width: '100%',
                          borderRadius: 0,
                          display: 'block',
                        }}
                      />
                    </Box>
                  ) : (
                    <EmptyState
                      type="no-data"
                      title={t('detail.noVideo')}
                      message={t('detail.noVideoSubtitle')}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Description */}
              <Card>
                <CardHeader
                  title={t('detail.description')}
                  icon={<Description />}
                />
                <CardContent>
                  <Typography variant="body1" color="text.primary">
                    {microconcepto.descripcion || t('card.noDescription')}
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={`${`${spacing.lg}px`}px`}>
              {/* Details Card */}
              <Card>
                <CardHeader
                  title={t('detail.details')}
                  icon={<EmojiObjects />}
                />
                <CardContent>
                  <Stack spacing={`${`${spacing.md}px`}px`}>
                    {microconcepto.Dificultad && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', marginBottom: `${spacing.xs}px` }}
                        >
                          {t('detail.difficulty.label')}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: `${spacing.sm}px`,
                          }}
                        >
                          <Rating
                            value={microconcepto.Dificultad}
                            max={3}
                            readOnly
                            size="small"
                          />
                          <Typography
                            variant="body2"
                            fontWeight={typography.fontWeight.semibold}
                            sx={{ color: getDifficultyColor(microconcepto.Dificultad) }}
                          >
                            {getDifficultyLabel(microconcepto.Dificultad)}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {microconcepto.tipo && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', marginBottom: `${spacing.xs}px` }}
                        >
                          {t('detail.type')}
                        </Typography>
                        <Typography variant="body2" fontWeight={typography.fontWeight.semibold}>
                          {microconcepto.tipo}
                        </Typography>
                      </Box>
                    )}

                    {microconcepto.posicion && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', marginBottom: `${spacing.xs}px` }}
                        >
                          {t('detail.position')}
                        </Typography>
                        <Typography variant="body2" fontWeight={typography.fontWeight.semibold}>
                          {microconcepto.posicion}
                        </Typography>
                      </Box>
                    )}

                    {userId && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', marginBottom: `${spacing.xs}px` }}
                        >
                          {t('detail.viewProgress')}
                        </Typography>
                        <Stack spacing={`${`${spacing.xs}px`}px`}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: `${spacing.xs}px` }}>
                            {watched ? (
                              <>
                                <CheckCircle sx={{ fontSize: 18, color: colors.success[500] }} />
                                <Typography
                                  variant="body2"
                                  fontWeight={typography.fontWeight.semibold}
                                  sx={{ color: colors.success[500] }}
                                >
                                  {t('card.watched')} {viewCount > 0 && `(${t('detail.watchedCount', { count: viewCount })})`}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Visibility sx={{ fontSize: 18, color: colors.neutral[400] }} />
                                <Typography
                                  variant="body2"
                                  fontWeight={typography.fontWeight.semibold}
                                  sx={{ color: colors.neutral[400] }}
                                >
                                  {t('detail.pending')}
                                </Typography>
                              </>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {secondsWatched > 0
                              ? `${Math.round(secondsWatched)} ${t('detail.secondsWatched')}`
                              : t('detail.noProgress')}
                          </Typography>
                          {watched && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={resetWatched}
                              sx={{
                                fontSize: typography.fontSize.xs,
                                color: colors.neutral[500],
                                borderColor: 'divider',
                                textTransform: 'none',
                                padding: `${spacing.xs} ${spacing.sm}`,
                                '&:hover': {
                                  borderColor: colors.neutral[500],
                                  backgroundColor: 'background.default',
                                },
                              }}
                              startIcon={<VisibilityOff sx={{ fontSize: 14 }} />}
                            >
                              {t('detail.unmarkWatched')}
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {/* Key Points (if available) */}
              {microconcepto.puntosClave && microconcepto.puntosClave.length > 0 && (
                <Card>
                  <CardHeader
                    title={t('detail.keyPoints')}
                    icon={<Star />}
                  />
                  <CardContent>
                    <List>
                      {microconcepto.puntosClave.map((punto, index) => (
                        <ListItem key={index} disablePadding>
                          <ListItemText
                            primary={punto}
                            primaryTypographyProps={{
                              variant: 'body2',
                              color: 'text.primary',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AnalisisIndividualDetalleModern;
